import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ChartBar, DownloadSimple, CaretDown, CaretRight, User } from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const currentMonth = () => new Date().toISOString().slice(0, 7);

function Kpi({ label, value }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg p-2 text-center" data-testid={`toolbox-kpi-${label}`}>
      <p className="font-heading font-black text-2xl text-blue-900 leading-none">{value}</p>
      <p className="text-[10px] uppercase tracking-wide text-slate-500 mt-1">{label}</p>
    </div>
  );
}

function PersonRow({ p, idx }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-slate-100" data-testid={`toolbox-person-${idx}`}>
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-slate-50"
        data-testid={`toolbox-person-toggle-${idx}`}>
        {open ? <CaretDown size={13} className="text-slate-400" /> : <CaretRight size={13} className="text-slate-400" />}
        <User size={14} className="text-blue-900 shrink-0" />
        <span className="flex-1 min-w-0">
          <span className="font-semibold text-slate-900 text-sm">{p.name}</span>
          <span className="text-[10px] text-slate-500 ml-2">DNI {p.id_number || "-"} · {p.position || "-"}</span>
        </span>
        <span className="font-heading font-black text-blue-900 text-sm">{p.count}</span>
        <span className="text-[10px] text-slate-400">charla(s)</span>
      </button>
      {open && (
        <div className="bg-slate-50 px-9 py-2 space-y-1" data-testid={`toolbox-person-talks-${idx}`}>
          {(p.talks || []).map((t, i) => (
            <div key={i} className="text-xs text-slate-700 flex items-center gap-2">
              <span className="font-mono text-slate-500">{t.date}</span>
              <span className="font-semibold">{t.topic}</span>
              <span className="text-slate-400">· {t.presenter || "—"}{t.area ? ` · ${t.area}` : ""}</span>
              {t.signed && <span className="text-[9px] font-bold text-emerald-700">FIRMÓ</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ToolboxMonthlyDialog({ open, onClose }) {
  const [month, setMonth] = useState(currentMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    if (!open) return;
    setLoading(true);
    api.get(`/hse/toolbox/summary?month=${month}`).then((r) => setData(r.data)).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  }, [open, month]);
  useEffect(() => { load(); }, [load]);

  const downloadPdf = async () => {
    try {
      const r = await api.get(`/hse/toolbox/summary/pdf?month=${month}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `charlas-5min-${month}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  const downloadXlsx = async () => {
    try {
      const r = await api.get(`/hse/toolbox/summary/xlsx?month=${month}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `charlas-5min-asistencia-${month}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-white max-w-3xl max-h-[88vh] overflow-y-auto" aria-describedby="toolbox-report-desc">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <ChartBar size={20} weight="duotone" className="text-blue-900" /> Reporte mensual · Charlas de 5 min
          </DialogTitle>
          <p id="toolbox-report-desc" className="text-xs text-slate-500">Acumulado del mes y asistencia por persona.</p>
        </DialogHeader>

        <div className="flex flex-wrap items-end gap-2 mb-3">
          <div>
            <Label className="text-[10px] uppercase">Mes</Label>
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-8" data-testid="toolbox-report-month" />
          </div>
          <Button size="sm" onClick={downloadPdf} disabled={!data || data.total_talks === 0}
            className="h-8 gap-1 bg-blue-900 hover:bg-blue-800 text-white" data-testid="toolbox-report-pdf">
            <DownloadSimple size={13} weight="bold" /> PDF
          </Button>
          <Button size="sm" onClick={downloadXlsx} disabled={!data || data.total_talks === 0}
            className="h-8 gap-1 bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="toolbox-report-xlsx">
            <DownloadSimple size={13} weight="bold" /> Excel (nómina)
          </Button>
        </div>

        {loading && <p className="text-center text-slate-400 text-sm py-8">Calculando…</p>}

        {data && !loading && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4" data-testid="toolbox-report-kpis">
              <Kpi label="Charlas" value={data.total_talks} />
              <Kpi label="Asistencias" value={data.total_attendance} />
              <Kpi label="Personas" value={data.unique_participants} />
              <Kpi label="Minutos" value={data.total_minutes} />
            </div>

            <h3 className="font-heading font-bold text-blue-900 uppercase text-[11px] tracking-widest mb-1">Asistencia por persona</h3>
            <div className="border border-slate-200 rounded-lg overflow-hidden mb-4" data-testid="toolbox-report-persons">
              {data.per_person.length === 0 && <p className="text-xs text-slate-400 text-center py-5">Sin asistentes registrados este mes.</p>}
              {data.per_person.map((p, i) => <PersonRow key={i} p={p} idx={i} />)}
            </div>

            {data.by_area.length > 0 && (
              <div className="flex flex-wrap gap-1.5" data-testid="toolbox-report-areas">
                {data.by_area.map((a) => (
                  <span key={a.area} className="text-[11px] bg-slate-100 text-slate-700 rounded-full px-2.5 py-0.5">{a.area}: <b>{a.count}</b></span>
                ))}
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
